import { useState, useEffect, useMemo, useCallback } from 'react';
import type { Order } from '../services/orderService';
import type { CustomerKind } from '../services/customerService';

// Versioned key — bump the suffix on any breaking change to OrderFilterState's
// shape so a stale persisted blob (missing newly-required arrays like `tags`)
// is dropped rather than merged into a half-populated state. v2 adds `tags`.
const STORAGE_KEY = 'twm_orders_filter_state_v2';

export type BooqableStatus = Order['booqableStatus'];
export type PaymentStatus = Order['paymentStatus'];

export interface OrderFilterState {
  statuses: BooqableStatus[];
  paymentStatuses: PaymentStatus[];
  /** ISO date string (YYYY-MM-DD) or empty. Inclusive lower bound on the
   *  rental period — orders whose endDate is before this are excluded. */
  dateFrom: string;
  /** ISO date string (YYYY-MM-DD) or empty. Inclusive upper bound on the
   *  rental period — orders whose startDate is after this are excluded. */
  dateUntil: string;
  customerKinds: CustomerKind[];
  /** Selected order tags (Booqable-native, e.g. "web-order"). OR semantics —
   *  an order matches if it carries ANY selected tag. The SANDBOX system tag
   *  is never offered in the UI. (#20) */
  tags: string[];
  /** Filter sidebar open/closed. Owned here (not by useLayout) so the user's
   *  choice persists across navigation; LeftRail's onNavigate would otherwise
   *  re-open it on every visit. */
  sidebarCollapsed: boolean;
}

const EMPTY_STATE: OrderFilterState = {
  statuses: [],
  paymentStatuses: [],
  dateFrom: '',
  dateUntil: '',
  customerKinds: [],
  tags: [],
  sidebarCollapsed: false,
};

function loadState(): OrderFilterState {
  if (typeof window === 'undefined') return EMPTY_STATE;
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const base = raw ? { ...EMPTY_STATE, ...JSON.parse(raw) } : EMPTY_STATE;
    return isMobile ? { ...base, sidebarCollapsed: true } : base;
  } catch {
    return isMobile ? { ...EMPTY_STATE, sidebarCollapsed: true } : EMPTY_STATE;
  }
}

function saveState(state: OrderFilterState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota / private mode — silently fail.
  }
}

export interface ApplyFiltersOptions {
  /** Optional resolver from a customer id to its kind (individual / contact /
   *  company). Used by the customer-type filter; if omitted or it returns
   *  undefined for an order, that order is treated as 'individual' (the
   *  customerService default). */
  getCustomerKind?: (customerId: string | null) => CustomerKind | undefined;
}

export interface UseOrderFiltersResult {
  state: OrderFilterState;
  setField: <K extends keyof OrderFilterState>(key: K, value: OrderFilterState[K]) => void;
  clearAll: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
  applyFilters: (orders: Order[], options?: ApplyFiltersOptions) => Order[];
}

export function useOrderFilters(): UseOrderFiltersResult {
  const [state, setState] = useState<OrderFilterState>(loadState);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const setField = useCallback(
    <K extends keyof OrderFilterState>(key: K, value: OrderFilterState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const clearAll = useCallback(() => {
    setState((prev) => ({ ...EMPTY_STATE, sidebarCollapsed: prev.sidebarCollapsed }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (state.statuses.length > 0) n++;
    if (state.paymentStatuses.length > 0) n++;
    if (state.dateFrom || state.dateUntil) n++;
    if (state.customerKinds.length > 0) n++;
    if (state.tags.length > 0) n++;
    return n;
  }, [state]);

  const hasActiveFilters = activeFilterCount > 0;

  const applyFilters = useCallback<UseOrderFiltersResult['applyFilters']>(
    (orders, options) => {
      const fromTs = state.dateFrom ? new Date(state.dateFrom).getTime() : null;
      // Until is inclusive — push to end-of-day so a YYYY-MM-DD upper bound
      // includes orders that start anywhere on that calendar day.
      const untilTs = state.dateUntil
        ? new Date(`${state.dateUntil}T23:59:59`).getTime()
        : null;

      return orders.filter((o) => {
        if (state.statuses.length > 0) {
          if (!state.statuses.includes(o.booqableStatus)) return false;
        } else if (o.booqableStatus === 'canceled' || o.booqableStatus === 'archived') {
          // Default view hides terminal-state orders; staff opt in by checking
          // Canceled/Archived in the sidebar.
          return false;
        }
        if (
          state.paymentStatuses.length > 0 &&
          !state.paymentStatuses.includes(o.paymentStatus)
        ) {
          return false;
        }

        // Date range — overlap semantic. Include the order if its rental
        // period [startDate, endDate] intersects [from, until]. A missing
        // bound on either side is treated as open-ended.
        if (fromTs !== null) {
          const endTs = o.endDate ? new Date(o.endDate).getTime() : null;
          if (endTs !== null && endTs < fromTs) return false;
        }
        if (untilTs !== null) {
          const startTs = o.startDate ? new Date(o.startDate).getTime() : null;
          if (startTs !== null && startTs > untilTs) return false;
        }

        if (state.customerKinds.length > 0) {
          const kind = options?.getCustomerKind?.(o.customerId) || 'individual';
          if (!state.customerKinds.includes(kind)) return false;
        }

        // Tags — OR semantics: keep the order if it carries ANY selected tag.
        if (state.tags.length > 0) {
          const orderTags = o.tagList || [];
          if (!state.tags.some((t) => orderTags.includes(t))) return false;
        }

        return true;
      });
    },
    [state],
  );

  return { state, setField, clearAll, hasActiveFilters, activeFilterCount, applyFilters };
}
